"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";

export default function AboutSection() {
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, amount: 0.1 });

	return (
		<section ref={ref} className="py-16 md:py-24 bg-white">
			<div className="container mx-auto px-4">
				<div className="max-w-4xl mx-auto">
					{/* Introduction Paragraphs */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
						transition={{ duration: 0.6, ease: "easeOut" }}
						className="space-y-6 mb-12"
					>
						<p className="text-gray-700 leading-relaxed">
							Every Great Player Starts With A Dream — The Dream To Touch The
							Ball, Wear The Jersey, And Make Their Nation Proud. Reliance
							Future Football Academy (RFFA) Was Born From This Belief: That
							With The Right Guidance, Discipline, Education, And Opportunity,
							Young Africans Can Compete And Excel On The Global Stage.
						</p>

						<p className="text-gray-700 leading-relaxed">
							At RFFA, We Go Beyond Football Training. We Develop Champions In
							Character, Intellect, And Talent. Our Academy Uniquely Blends
							Professional Football Coaching With Quality Education, Ensuring
							Every Player Grows As Both An Athlete And A Well-Rounded
							Individual Prepared For Life Beyond The Pitch. We Believe True
							Excellence Is Achieved When Academic Brilliance Meets Sporting
							Passion.
						</p>

						<p className="text-gray-700 leading-relaxed">
							We Welcome Boys Aged 14 To 16 Who Demonstrate Promise Both
							Academically And Athletically. Through Our Carefully Designed
							Programs, Players Receive Elite Football Training While Pursuing
							Their Education. Our Dual-Pathway Model Creates Opportunities For
							Academic Placement And Football Progression Abroad, Particularly
							In The United States, Where Players Continue To Learn, Compete,
							And Thrive.
						</p>

						<p className="text-gray-700 leading-relaxed">
							Our Story Is One Of Hope, Discipline, And Transformation. Every
							Training Session, Lesson, And Match Is A Step Toward Building
							Confident, Globally Minded Student-Athletes Who Will Make Africa
							Proud On And Off The Field.
						</p>

						<p className="text-gray-700 leading-relaxed">
							Reliance Future Football Academy — Catching Them Young.
						</p>
					</motion.div>

					{/* Mission Statement */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
						transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
						className="mb-10"
					>
						<h2 className="text-xl font-semibold text-primary mb-3">
							Mission Statement
						</h2>
						<p className="text-gray-700 leading-relaxed">
							Our mission at Reliance Future Football Academy (RFFA) is to
							deliver a holistic football development program that nurtures
							talent, instills strong values, and builds a vibrant educational
							and sporting community. We are committed to developing
							disciplined, responsible, and respectful players who excel both in
							football and in life.
						</p>
					</motion.div>

					{/* Core Focus Areas */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
						transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
						className="mb-10"
					>
						<h2 className="text-xl font-semibold text-primary mb-3">
							Core Focus Areas
						</h2>
						<p className="text-gray-700 leading-relaxed mb-4">
							RFFA&apos;s developmental philosophy is anchored on six key
							pillars:
						</p>
						<ol className="text-gray-700 space-y-2 list-decimal list-inside">
							<li>
								Player Development: Enhancing technical, tactical, physical, and
								mental abilities
							</li>
							<li>
								Quality Coaching: Professional, modern, and adaptive training
								methods
							</li>
							<li>
								Inclusive Culture: A supportive and inspiring environment for
								all players
							</li>
							<li>
								Clear Communication & Transparency: Trust built on integrity and
								accountability
							</li>
							<li>
								Safety First: Prioritizing player health, welfare, and
								protection
							</li>
							<li>
								Parental Engagement: Partnering with parents for long-term
								player success
							</li>
						</ol>
					</motion.div>

					{/* Our Values */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
						transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
						className="mb-10"
					>
						<h2 className="text-xl font-semibold text-primary mb-3">
							Our Values
						</h2>
						<p className="text-gray-700 leading-relaxed mb-4">
							RFFA is guided by values that define our culture and identity:
						</p>
						<ul className="text-gray-700 space-y-2">
							<li className="flex items-start">
								<span className="mr-3">-</span>
								<span>
									Respect – for the game, teammates, coaches, officials, and
									community.
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-3">-</span>
								<span>
									Discipline – maintaining focus, commitment, and responsibility
									on and off the pitch.
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-3">-</span>
								<span>
									Teamwork – fostering collaboration, unity, and collective
									achievement.
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-3">-</span>
								<span>
									Excellence – striving for the highest standards in training,
									performance, and personal conduct.
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-3">-</span>
								<span>
									Integrity – upholding honesty, fairness, and transparency in
									every action.
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-3">-</span>
								<span>
									Positive Attitude – approaching challenges with resilience,
									optimism, and determination.
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-3">-</span>
								<span>
									Ambition & Humility – pursuing success with confidence while
									remaining grounded and respectful.
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-3">-</span>
								<span>
									Continuous Improvement – encouraging players and the academy
									to learn, grow, and evolve consistently.
								</span>
							</li>
						</ul>
					</motion.div>

					{/* Our Promise */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
						transition={{ duration: 0.6, delay: 0.25, ease: "easeOut" }}
						className="mb-10"
					>
						<h2 className="text-xl font-semibold text-primary mb-3">
							Our Promise
						</h2>
						<p className="text-gray-700 leading-relaxed mb-6">
							At Reliance Future Football Academy, we provide clear and
							structured pathways that combine football excellence with academic
							advancement:
						</p>

						<div className="space-y-6">
							<div>
								<h3 className="font-semibold text-gray-900 mb-1">
									Academic Pathway Abroad
								</h3>
								<p className="text-gray-700 leading-relaxed">
									Admission opportunities into reputable high schools abroad
									while continuing structured football development.
								</p>
							</div>

							<div>
								<h3 className="font-semibold text-gray-900 mb-1">
									Professional Football Pathway
								</h3>
								<p className="text-gray-700 leading-relaxed">
									Exposure to international scouts and opportunities with top
									football clubs abroad for outstanding talents.
								</p>
							</div>
						</div>
					</motion.div>

					{/* Commitment */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
						transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
						className="mb-10"
					>
						<h2 className="text-xl font-semibold text-primary mb-3">
							Commitment
						</h2>
						<p className="text-gray-700 leading-relaxed">
							RFFA is dedicated to promoting fair play, strengthening community
							ties, and supporting players through both success and challenges.
							Through football and education, we shape individuals who are
							prepared to succeed in sport and in life.
						</p>
					</motion.div>

					{/* Programs/Age Groups */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
						transition={{ duration: 0.6, delay: 0.35, ease: "easeOut" }}
						className="mb-10"
					>
						<h2 className="text-xl font-semibold text-primary mb-3">
							Programs/ Age Groups: (Ages 14 – 16 Years)
						</h2>
						<p className="text-gray-700 leading-relaxed mb-4">
							Our program is designed for talented boys aged 14 to 16 who
							demonstrate strong football ability alongside academic excellence.
							Admission is based on both athletic potential and intellectual
							capacity.
						</p>
						<p className="text-gray-700 leading-relaxed">
							Selected players gain access to opportunities for admission into
							reputable middle and high schools abroad, particularly in the
							United States, where they can continue their education while
							advancing their football careers. In addition, players will be
							exposed to international scouting networks, creating pathways for
							potential trials and signing opportunities with top football clubs
							abroad. This dual-track approach ensures the development of
							globally competitive student-athletes.
						</p>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
